
import Vue from "vue";
import _ from "lodash";
import { mapActions, mapMutations } from "vuex";
import SingleSelect from "../form/SingleSelect.vue";
import GuidanceBadge from "../guidance/GuidanceBadge.vue";
import avsCheck from "~/assets/utils/Avs/avsCheck";
import mapState from "~/assets/utils/mapState";
import resolveStateCountryObjects, {
  isUnresolvedDetail,
  listsAreReady,
} from "~/assets/utils/resolveStateCountryObjects";
import parseStateLabel from "~/assets/utils/parseStateLabel";
import { toSingleLine } from "~/assets/utils/getAvsParams";
import type { GuidanceState } from "~/utils/guidanceTypes";
import { DetailsSource, AVSStatus, AVSSubStatus, AvsStatusEnum } from "~/entities/AVS";
import { Details } from "~/entities/Details";
import Place from "~/entities/Place";
import { Country, State } from "~/entities/Response/FormData";
import SavedAddress from "~/entities/SavedAddress";
import * as Sentry from "@sentry/vue";

export default Vue.extend({
  components: {
    SingleSelect,
    GuidanceBadge,
  },
  props: {
    validated: Boolean,
    label: {
      type: String,
      default: "",
    },
    googleObj: {
      type: Object,
      default: null,
    },
    preSelection: {
      type: String,
      default: "",
    },
    errors: {
      type: Boolean,
      default: false,
    },
    equal: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array as () => Array<{ id: string; label: string }>,
      default: () => [],
    },
    userAddresses: {
      type: Array as () => Array<SavedAddress>,
      default: () => [],
    },
    id: {
      type: Number,
      default: 0,
    },
    direction: {
      type: String,
      default: "from",
    },
    allowedCountries: {
      type: Array<Country>,
      default: () => [],
    },
    states: {
      type: Array<State>,
      default: () => [],
    },
    warning: {
      type: Boolean,
      default: false,
    },
    avsState: {
      type: String,
      default: "idle",
    },
    avsMessage: {
      type: String,
      default: "",
    },
    // BIK-2107: true when GetRates failed for this trip; reveals the segmented
    // form so the user can correct the address (under avsRawPasteDirect).
    ratesFailed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      autocomplete: {} as google.maps.places.Autocomplete,
      location: null as null | string,
      // BIK-2107: dedicated Street Address field (fallback form). Local mirror of the
      // store's address1/streetAddress; the main input above stays the paste/search buffer.
      streetAddress: null as null | string,
      city: null as null | string,
      state: null as any,
      country: null as any,
      postalCode: null as null | string,
      unit: null as null | string,
      isSingle: true as boolean,
      isOpen: false as boolean,
      show: false as boolean,
      disabled: false,
      place: {} as Place,
      processing: false,
      isSavedAddress: false,
      skipEmitCheck: false,
      // BIK-2125: a `change` on the main single input was gated (autofill/password-manager
      // programmatic change while focus was still inside — Google gets priority). Set true
      // so a later real blur without picking a suggestion still runs the deferred AVS Region
      // check (checkTrip), since the browser won't re-fire `change` for the unchanged value.
      pendingMainInputAvs: false as boolean,
      suggestions: [] as google.maps.places.AutocompletePrediction[],
      highlightedIndex: -1,
      filteredUserAddress: [] as SavedAddress[],
      isOpenSuggestions: false,
      // BIK-2106: which input-method icon is currently the active/highlighted one. Google is
      // the default (typing → Google lookup). googleModeActive turns on when the user opens
      // Google (icon click) or types; it stays on after picking a suggestion so the icon keeps
      // reflecting how the address was entered, and only resets on clear / paste / manual edit.
      // It also enables live fetchSuggestions-on-input (BIK-2095 left typing/pasting without
      // live Google suggestions, so this must not leak into the default paste flow).
      googleModeActive: false as boolean,
      // BIK-2106: highlights the Paste icon. Set when a paste is detected — native Ctrl+V or
      // an explicit Paste-icon click (both run the same direct-AVS flow). Mutually exclusive
      // with googleModeActive; cleared on typing / google / clear.
      pasteModeActive: false as boolean,
      // BIK-2108: once the user X-clears the auto-prefilled preferred Ship From, don't
      // re-prefill it for the rest of the session (Bill: "X out and enter a different one").
      // Guards prefillPreferred() so the userAddresses watcher can't re-fill after a manual clear.
      preferredCleared: false as boolean,
      // BIK-2108: set synchronously the first time prefillPreferred() actually fires the prefill,
      // so a second call (mounted + watcher, or a watcher re-fire) can't kick off a duplicate
      // saved-address resolve/AVS while the first is still in flight (location is only set async).
      preferredPrefilled: false as boolean,
      // BIK-2106: true when the user explicitly opened the dropdown via the Saved (▼) icon,
      // vs. it being opened by typing. Typing must NOT show an empty panel (Bill: the
      // "powered by Google" tray pre-showed empty before 3 chars); an explicit ▼ click may
      // open it even with nothing (saved/events) to preserve the old chevron behavior.
      suggestionsOpenedExplicitly: false as boolean,
      isAddressExpanded: false,
      // BIK-2107: sticky flag for the incomplete-paste fallback. Once AVS reveals the
      // segmented form because the address couldn't be resolved, this stays true while
      // the user fills the fields — surviving the per-edit AVS resets (which drop the
      // transient "errorIncomplete" state) — so the yellow "needs input" highlight keeps
      // pointing at the still-empty fields. Cleared on verify / clear input.
      inSegmentedFallback: false as boolean,
      searchInputValue: "" as string,
      showArrowBtn: false as boolean,
      needSetAddress: true as boolean,
      isLimit: false as boolean,
      isLoadingInput: false as boolean,
      allowedState: [] as any,
      filterStates: [] as any,
      // BIK-2097: tracks how the current address was entered, set by the entry
      // points (paste / suggestion select / manual edit) and forwarded to the
      // store so it gets logged via /api/log.
      inputMethod: null as null | string,
      // BIK-2097: the exact text the user pasted/typed before Google Places
      // resolved it, captured alongside inputMethod.
      rawInput: null as null | string,
    };
  },
  computed: {
    placeholder(): string {
      return this.label;
    },
    // BIK-2108: the user's preferred/default Ship From — the first saved address flagged
    // isPrimary on /youraddresses ("Preferred ship from address"). null when none is flagged.
    // Only meaningful under avsRawPasteDirect; legacy never surfaced isPrimary in the funnel.
    preferredAddress(): SavedAddress | null {
      if (!(this as any).avsRawPasteDirect) return null;
      return this.userAddresses?.find((a) => a?.isPrimary === true) ?? null;
    },
    // BIK-2108: the saved-address list with the preferred entry hoisted to the top (stable
    // order for the rest), so it shows first in BOTH the From and To dropdowns. Flag OFF (or
    // no preferred) returns the list untouched, preserving legacy ordering.
    orderedUserAddresses(): SavedAddress[] {
      const list = this.userAddresses ?? [];
      const preferred = (this as any).preferredAddress as SavedAddress | null;
      if (!preferred) return list;
      return [preferred, ...list.filter((a) => a !== preferred)];
    },
    // BIK-2106 (Bill): saved addresses only belong in the panel when the user opened it
    // explicitly via the Saved (▼) icon — NOT while typing a Google lookup, where the panel
    // should list only Google suggestions. `suggestionsOpenedExplicitly` is true only for the
    // ▼/focus open path and false for the typing path (onMainInput). Legacy (flag OFF) keeps
    // saved addresses visible in all cases.
    showSavedAddresses(): boolean {
      if (!this.filteredUserAddress?.length) return false;
      if (!(this as any).avsRawPasteDirect) return true;
      return this.suggestionsOpenedExplicitly;
    },
    // BIK-2106: is there anything to list in the dropdown (Google suggestions, saved
    // addresses that are currently allowed to show, or event addresses)?
    hasDropdownContent(): boolean {
      return (
        (this.suggestions?.length ||
          ((this as any).showSavedAddresses ? this.filteredUserAddress?.length : 0) ||
          this.options?.length) > 0
      );
    },
    // BIK-2106 (Bill): don't pre-show an empty tray. Under the flag the panel renders only
    // when it has content, unless the user opened it explicitly via the Saved (▼) icon.
    // Legacy (flag OFF) keeps the original always-open-on-focus behavior.
    showSuggestionsPanel(): boolean {
      if (!this.isOpenSuggestions) return false;
      if (!(this as any).avsRawPasteDirect) return true;
      return (this as any).hasDropdownContent || this.suggestionsOpenedExplicitly;
    },
    hasAdvancedInStep(): boolean {
      return this.$store.state.ship.hasAdvancedInStep;
    },
    // BIK-2095: paste-direct-to-AVS flag (keep pasted text, verify via UPS Region mode).
    avsRawPasteDirect(): boolean {
      return (this as any).$config?.avsRawPasteDirect === true;
    },
    isAddress1Invalid(): boolean {
      return (
        this.avsState === "errorAddress" ||
        this.avsState === "errorOutOfRange" ||
        // BIK-2148: errorNotActive dropped — it never blocked Search, so painting the
        // field red told the user to fix something that was not stopping them. It is a
        // heads-up (amber), handled with the warning states below.
        this.avsState === "errorCarrierRestricted" // BIK-2135
      );
    },
    isAddress2Invalid(): boolean {
      return this.avsState === "unitRequired";
    },
    // BIK-2107: address-level AVS errors that should surface right under the main
    // Ship From/To input (not below the Apt/Unit field). unitRequired stays near
    // the Apt/Unit field. Gated by the new flag so the legacy layout is unchanged.
    showAddressMsgUnderInput(): boolean {
      return (
        (this as any).avsRawPasteDirect &&
        (this.avsState === "errorAddress" ||
          this.avsState === "errorOutOfRange" ||
          this.avsState === "errorNotActive" ||
          this.avsState === "errorIncomplete")
      );
    },
    // BIK-2107: while in the incomplete-paste fallback, suppress the legacy per-field
    // red (native :invalid on empty required inputs) — we guide with yellow instead.
    // Uses the sticky flag so it holds across the per-edit AVS resets, not just the
    // instant AVS returned "errorIncomplete". Gated so legacy validation is untouched.
    suppressFieldErrors(): boolean {
      return (this as any).avsRawPasteDirect && this.inSegmentedFallback;
    },
    showVerifiedBadge(): boolean {
      return this.avsState === "verified" && !this.isLoadingInput;
    },
    // BIK-2107: are we in the incomplete-paste fallback (sticky, survives edits)?
    isIncompleteFallback(): boolean {
      return (this as any).avsRawPasteDirect && this.inSegmentedFallback;
    },
    // BIK-2107: per-field "action required" (yellow) — a required field still empty
    // while completing the fallback. Each field clears to neutral as it's filled, so
    // the remaining yellow shows exactly what's left; all flip to green on verify.
    streetNeedsInput(): boolean {
      return (this as any).isIncompleteFallback && !this.streetAddress;
    },
    cityNeedsInput(): boolean {
      return (this as any).isIncompleteFallback && !this.city;
    },
    stateNeedsInput(): boolean {
      return (this as any).isIncompleteFallback && !this.state?.stateprovcode;
    },
    postalNeedsInput(): boolean {
      return (this as any).isIncompleteFallback && !this.postalCode;
    },
    countryNeedsInput(): boolean {
      const code =
        typeof this.country === "string" ? this.country : this.country?.code;
      return (this as any).isIncompleteFallback && !code;
    },
    // BIK-2107: the message paragraph under the main input also covers the verified
    // state so "Verified!" sits right under Ship From, not below the Apt/Unit field.
    showMsgUnderInput(): boolean {
      return (
        (this as any).showAddressMsgUnderInput ||
        (this as any).isIncompleteFallback ||
        ((this as any).avsRawPasteDirect && this.avsState === "verified")
      );
    },
    // BIK-2107: the text shown under the main input. While completing the fallback the
    // AVS state resets to idle (empty avsMessage), so keep a steady "please complete"
    // prompt until the address verifies.
    underInputMessage(): string {
      if (this.avsMessage) return this.avsMessage;
      if ((this as any).isIncompleteFallback)
        return "Please complete the address below.";
      return "";
    },
    // BIK-2106 (Bill): the "please complete the address below" prompt is an actionable alert
    // pointing at the segmented fields the user must fill — not a status like Verified/L+G/Cubic.
    // So it renders left-aligned BELOW the main input (next to the fields it refers to), not in
    // the top-right status slot. This flag identifies that specific state; every other AVS state
    // (verified, apiFailed, hard errors) stays in the top-right GuidanceBadge.
    showCompleteAddressNudge(): boolean {
      return (
        (this as any).avsRawPasteDirect &&
        ((this as any).isIncompleteFallback ||
          this.avsState === "errorIncomplete") &&
        !!(this as any).underInputMessage
      );
    },
    // BIK-2125: only the success ("Verified") status belongs in the top-right slot above the
    // main input. Errors/warnings (not-active-for-delivery, invalid address, unit required,
    // provider failure) are problems the user should read next to the field they fix, so they
    // move BELOW the main input instead of sitting up top as a red banner.
    showAvsBadgeAbove(): boolean {
      return (
        (this as any).avsRawPasteDirect &&
        this.avsState === "verified" &&
        !!(this as any).underInputMessage
      );
    },
    // BIK-2125: every non-verified AVS message (hard errors + warnings) renders left-aligned
    // below the main input. The "please complete the address" nudge already lived here
    // (showCompleteAddressNudge); this generalizes that slot to all error/warning states so
    // nothing but "Verified" shows above. Excludes the nudge case so it isn't rendered twice.
    showAvsBadgeBelow(): boolean {
      return (
        (this as any).avsRawPasteDirect &&
        !!(this as any).avsGuidanceState &&
        this.avsState !== "verified" &&
        !(this as any).showCompleteAddressNudge &&
        !!(this as any).underInputMessage
      );
    },
    // BIK-2107: map the AVS state to a GuidanceState so the feedback can render with the
    // shared GuidanceBadge (same weight/color/icon as the dims guidance), top-right above
    // the field. Null when there's nothing to show. Only used under avsRawPasteDirect.
    avsGuidanceState(): GuidanceState | null {
      if (this.avsState === "verified") return "verified";
      // BIK-2148: "warn" (amber), not "safe". `safe` is green in this palette
      // (guidance.safe = #3c763d on #dff0d8), which reads as "verified" — and nothing was
      // verified here. Amber is the same weight the incomplete/apiFailed states use: not
      // an error to fix now, but a heads-up that something is still pending — the full
      // street address at step 3.
      if (this.avsState === "zoneOnly") return "warn";
      if (
        this.avsState === "errorIncomplete" ||
        this.avsState === "apiFailed" ||
        // BIK-2148: a UPS Store is deliverable — a heads-up, never a block.
        this.avsState === "upsStoreFrom" ||
        this.avsState === "upsStoreTo" ||
        // BIK-2148: errorNotActive is a warning, not a stop. AVS could not confirm the
        // address is active for delivery, but it never blocked Search — and red
        // everywhere else in this form means "you must fix this before continuing".
        this.avsState === "errorNotActive" ||
        (this as any).isIncompleteFallback
      )
        return "warn";
      if (
        this.avsState === "unitRequired" ||
        this.avsState === "errorAddress" ||
        this.avsState === "errorOutOfRange" ||
        this.avsState === "errorCarrierRestricted" // BIK-2135
      )
        return "danger";
      return null;
    },
    avsMessageClass(): string {
      // BIK-2107: "incomplete" is a "please finish" prompt, not an error — warn (yellow),
      // matching the yellow field highlights. The sticky fallback (idle while filling)
      // keeps the warn color too. apiFailed also stays warn.
      if (
        this.avsState === "apiFailed" ||
        this.avsState === "errorIncomplete" ||
        // BIK-2148: see avsGuidanceState above — non-blocking, so amber not red.
        this.avsState === "errorNotActive" ||
        this.avsState === "upsStoreFrom" ||
        this.avsState === "upsStoreTo" ||
        (this as any).isIncompleteFallback
      )
        return "text-guidance-warn-text";
      if (this.avsState === "verified") return "text-guidance-verified";
      // BIK-2148: amber — see avsGuidanceState above.
      if (this.avsState === "zoneOnly") return "text-guidance-warn-text";
      if (
        this.avsState === "unitRequired" ||
        this.avsState === "errorAddress" ||
        this.avsState === "errorOutOfRange" ||
        this.avsState === "errorCarrierRestricted" // BIK-2135
      ) {
        return "text-guidance-danger-text";
      }
      return "";
    },
  },
  watch: {
    // BIK-2108: userAddresses arrives async (Search.getUserPresets → GET /api/Address) and
    // usually AFTER this component mounts, so the mounted() prefill attempt sees an empty prop.
    // Re-attempt when the list first populates. prefillPreferred()'s own guards (Ship From only,
    // empty field, not yet cleared, preferred exists) keep this idempotent and non-clobbering.
    userAddresses() {
      (this as any).prefillPreferred();
    },
    // BIK-2140: the country/state lists load async from Search.fetch() and can land before or
    // after userAddresses. Whichever completes last triggers the prefill; the earlier call is
    // a no-op thanks to prefillPreferred()'s own guards. No deep watch — the store commits a
    // new array, so the reference change is enough, and deep-walking 449 countries would not be.
    allowedCountries() {
      (this as any).onListsReady();
    },
    states() {
      (this as any).onListsReady();
    },
    hasAdvancedInStep(newVal) {
      if (newVal) {
        if (this.hasAdvancedInStep) {
          this.splitAddress();
        }
        this.isSingle = false;
        // BIK-2127: was `avsShip1Enabled ? !!this.unit : true`. AVS_SHIP1_REWORK was never
        // enabled, so the !!unit branch was dead code — always expand the full grid here.
        this.isAddressExpanded = true;
        this.showArrowBtn = true;
      }
    },
    country(newVal) {
      if (newVal) {
        this.allowedState = this.filterStates[newVal.code];
      }
    },
    isLoadingInput(newVal) {
      this.$emit("emitLoadingInput", newVal);
    },
    avsState(newVal) {
      // BIK-2107: reveal the segmented form so the user can fix the address.
      // unitRequired always expands (legacy behavior). Under the new paste-direct
      // flag, the user-fixable AVS failures also expand. apiFailed is a provider
      // error, not user-fixable, so it keeps the inline warning only.
      const revealStates = [
        "unitRequired",
        "errorAddress",
        "errorOutOfRange",
        "errorNotActive",
        "errorIncomplete",
      ];
      const shouldReveal =
        newVal === "unitRequired" ||
        ((this as any).avsRawPasteDirect && revealStates.includes(newVal));
      if (shouldReveal) {
        this.revealSegmented();
      }
      // BIK-2107: sticky incomplete-fallback UI state. Enter on "errorIncomplete".
      // The per-edit reset to "idle" (and the "verifying" spinner) must NOT clear it —
      // that's what keeps the yellow "needs input" guidance alive while the user fills
      // the fields. Any other terminal AVS result (verified → green, or a different
      // failure like errorAddress → red) hands control back to that state.
      if ((this as any).avsRawPasteDirect) {
        if (newVal === "errorIncomplete") {
          this.inSegmentedFallback = true;
        } else if (newVal !== "idle" && newVal !== "verifying") {
          // BIK-2107: if the address verified from the MAIN input (paste / one-line type
          // → Region mode) while the fallback form was open, the expanded segmented fields
          // are stale/empty — collapse back to the single verified line (as a normal paste
          // would look). If it verified by completing the segmented fields, stay expanded.
          const wasFallback = this.inSegmentedFallback;
          this.inSegmentedFallback = false;
          if (newVal === "verified") {
            if ((this as any).verifiedFromMainInput()) {
              // Verified from the main input (paste / one-line type / Google) while the
              // fallback was open → collapse back to the single verified line.
              if (wasFallback) {
                (this as any).collapseSegmented();
              }
            } else if (this.isAddressExpanded) {
              // BIK-2106: verified by completing the segmented fields (not the main input).
              // The main input above still shows the raw pre-fallback / previous text (e.g.
              // "…TX 12, US" after fixing a wrong ZIP), which now reads as stale next to the
              // verified, AVS-normalized address. Refresh the main input AND re-hydrate the
              // segmented fields from `details`. This must run on EVERY segmented re-verify,
              // not just the first exit from the fallback — so it's gated on the fields being
              // expanded, not on `wasFallback` (which is only true the first time).
              (this as any).refreshLocationFromDetails();
            }
          }
        }
      }
    },
    // BIK-2107: GetRates failed (trip-level) — reveal the segmented form on both
    // stages so the user can correct either address.
    ratesFailed(newVal) {
      if (newVal && (this as any).avsRawPasteDirect) {
        this.revealSegmented();
      }
    },
  },
  mounted() {
    if (this.preSelection) {
      this.searchInputValue = this.preSelection;
    }
    if (this.hasAdvancedInStep) {
      // BIK-2107: returning to step 1 (component re-mounts with hasAdvancedInStep).
      // Show only the street line in the main input; the grid below shows the rest.
      this.splitAddress(true);
      this.filterStates = mapState(this.states);
      this.allowedState = this.filterStates[this.country?.code];
    } else if(this.$store.state.ship.stages.trips?.[this.id]?.[this.direction]){
      if (this.$store.state.ship.stages.trips[this.id][this.direction].formatted_address) {
        this.location = this.$store.state.ship.stages.trips[this.id][this.direction].formatted_address
      }
    }
    // BIK-2108: if the saved addresses are already loaded when we mount, try to prefill the
    // preferred Ship From now. If they arrive later, the userAddresses watcher covers it.
    (this as any).prefillPreferred();
  },
  methods: {
    ...mapActions({
      requestResolve: "ship/requestResolve",
    }),
    // BIK-2140: used by reresolveUnresolved() to write the repaired country/state objects.
    ...mapMutations({
      setDetail: "ship/setDetail",
    }),
    async selectEventAddress(item: any): Promise<void> {
      if (item) {
        // BIK-2097: user picked a pre-filled event address.
        this.inputMethod = "event";
        this.rawInput = item?.displayName || item?.address1 || null;
        this.isLoadingInput = true;
        this.needSetAddress = false;
        this.show = true;
        this.isOpen = false;
        this.disabled = true;
        this.isOpenSuggestions = false;
      }
      try {
        await this.resolveEventLocation(item);
        this.disabled = false;
      } catch (error) {
        this.disabled = false;
      }
    },
    async selectUserAddress(item: any): Promise<void> {
      if (item) {
        // BIK-2097: user picked a previously saved address from the dropdown.
        this.inputMethod = "saved";
        this.rawInput = item?.displayName || item?.address1 || null;
        this.isLoadingInput = true;
        this.needSetAddress = false;
        this.show = true;
        this.isOpen = false;
        this.disabled = true;
        this.isOpenSuggestions = false;
      }
      try {
        await this.resolveUserLocation(item);
        this.disabled = false;
      } catch (error) {
        this.disabled = false;
      }
    },
    // BIK-2108: auto-prefill the Ship From field with the user's preferred/default address
    // (isPrimary) so shops shipping from their own location don't re-search every time
    // (Bill: could save 40%+ of repeated lookups). Delegates to selectUserAddress() so the
    // prefill goes through the exact same saved-address resolve + AVS path as a manual pick.
    // Guards (all must hold):
    //   - flag on (avsRawPasteDirect)
    //   - this is the Ship From field (never the Ship To)
    //   - not returning to step 1 with an expanded/edited address (hasAdvancedInStep)
    //   - no restored search / preSelection to preserve
    //   - the field is still empty (never clobber existing input)
    //   - the user hasn't already X-cleared a prefill this session (preferredCleared)
    //   - a preferred address actually exists
    // Called from mounted() and from the userAddresses watcher (the prop arrives async), so it
    // must be idempotent — the empty-field guard makes a second call a no-op after the first fills.
    prefillPreferred(): void {
      if (!(this as any).avsRawPasteDirect) return;
      if (this.direction !== "from") return;
      if (this.hasAdvancedInStep) return;
      if (this.preSelection) return;
      if (this.location) return;
      if (this.preferredCleared) return;
      if (this.preferredPrefilled) return;
      // BIK-2140: only the first stage's Ship From is the user's own origin. On a multi-stage
      // trip the later "from" fields are intermediate handoff points, so prefilling them with
      // the user's home address would be wrong.
      if (this.id !== 0) return;
      // BIK-2140: never prefill against empty lists. resolveUserLocation() matches country and
      // state against them and silently keeps the raw string ("US"/"TX") when they are empty,
      // which leaves the Rates payload with countryCode: "" and gets the request rejected.
      // Deliberately no timeout/fallback: if the lists never arrive we simply do not prefill.
      // The allowedCountries/states watchers below re-invoke this once they land.
      if (!listsAreReady((this as any).allowedCountries, (this as any).states))
        return;
      const preferred = (this as any).preferredAddress as SavedAddress | null;
      if (!preferred) return;
      this.preferredPrefilled = true;
      this.selectUserAddress(preferred);
    },
    // BIK-2140: single entry point for "the country/state lists just became usable".
    // Both halves of the fix hang off this: retry the prefill that its own guard blocked
    // while the lists were empty, and repair any detail that a path we do not control
    // (Google, paste) already resolved into raw strings before the lists existed.
    onListsReady(): void {
      if (!(this as any).avsRawPasteDirect) return;
      if (!listsAreReady((this as any).allowedCountries, (this as any).states))
        return;
      (this as any).prefillPreferred();
      (this as any).reresolveUnresolved();
    },
    // BIK-2140: repair a detail whose country/state were left as raw strings because the
    // allowed lists were still empty when it was resolved. Without this the degraded value
    // is permanent — the selects repopulate their options when the lists land, but nothing
    // ever re-runs the match, so the field stays blank and Rates keeps getting countryCode: "".
    //
    // Runs for both directions: a Ship To picked from a saved address degrades identically.
    //
    // Deliberately does NOT re-run avsCheck(). The address text is unchanged — only the shape
    // of two fields — so re-validating would burn a UPS call, race the in-flight check, and
    // risk flipping the "Verified" badge back. Committing just {country, state} lets
    // setDetail's merge keep avsStatus/passedAvs/avsChecked exactly as they were.
    reresolveUnresolved(): void {
      const detail =
        this.$store.state.ship.stages.details?.[this.id]?.[this.direction];
      if (!isUnresolvedDetail(detail)) return;
      // Resolve on a copy: mutating store state in place would bypass the mutation.
      const candidate = _.cloneDeep(detail);
      const { changed } = resolveStateCountryObjects(
        candidate,
        (this as any).allowedCountries,
        (this as any).states
      );
      // Nothing matched (e.g. a country with no states list, or a free-text state we should
      // not touch) — return without writing, so the deep StageDetails watcher in Search.vue
      // does not re-validate on every list tick.
      if (!changed) return;
      this.setDetail({
        direction: this.direction,
        value: { country: candidate.country, state: candidate.state },
        index: this.id,
      });
      // Keep the visible segmented selects in step with what we just resolved.
      this.country = candidate.country;
      this.state = candidate.state;
    },
    onShow(): void {
      this.isAddressExpanded = !this.isAddressExpanded;
      this.isSingle = !this.isAddressExpanded;
    },
    // BIK-2107: reveal the segmented City/State/Country/Postal grid as the manual
    // fallback on AVS/GetRates failure. splitAddress() repopulates every field
    // from the store, so the user's already-entered input is preserved.
    revealSegmented(): void {
      if (this.isSingle) {
        this.splitAddress();
      }
      this.isAddressExpanded = true;
      // BIK-2106: verification failed and we've dropped into the manual/segmented path —
      // close the Google suggestions dropdown and drop the Google highlight (the user now
      // fills the fields instead of picking a lookup result). Applies whether they got here
      // by typing (Google) or pasting.
      this.closeSuggestions();
      this.googleModeActive = false;
    },
    // BIK-2107: did the address verify from the MAIN input (paste / one-line type,
    // i.e. AVS Region mode) rather than from completing the segmented fields? Read the
    // inputMethod the store's detail carried into the verifying avsCheck.
    verifiedFromMainInput(): boolean {
      const detail =
        this.$store.state.ship.stages.details?.[this.id]?.[this.direction];
      const method = detail?.inputMethod;
      // BIK-2107/2106: the address came from the main search input rather than from
      // hand-completing the segmented fields — paste / one-line type (Region) OR a picked
      // Google suggestion. All should collapse the fallback back to the single line on
      // verify. Completing the segmented fields uses "manual" and stays expanded.
      return (
        method === "paste" ||
        method === "manual-single" ||
        method === "google"
      );
    },
    // BIK-2107: collapse the expanded fallback back to the single verified line (like a
    // normal paste), keeping the verified value in the main input. Does NOT wipe fields.
    collapseSegmented(): void {
      this.isAddressExpanded = false;
      this.isSingle = true;
      this.showArrowBtn = false;
    },
    // BIK-2106: after verifying via the completed segmented fallback fields, the main
    // input still shows whatever raw text the user typed before AVS failed the first
    // time (e.g. "123") — replace it with the full formatted address, same source
    // (Place.getFormattedAddress) splitAddress() already uses for the normal case.
    // BIK-2106: also re-hydrate the still-visible segmented fields from the AVS-normalized
    // `details`, so AVS corrections (ZIP+4, abbreviations, casing) show consistently there
    // too — not just in the main input. Without this the segmented Postal kept the user's
    // typed "55343" while the main input showed the normalized "55343-7804". Mirrors the
    // field mapping in splitAddress(); does NOT change the view's expand/collapse state.
    refreshLocationFromDetails(): void {
      const details =
        this.$store.state.ship.stages.details?.[this.id]?.[this.direction];
      if (!details) return;
      const formatted = Place.getFormattedAddress(details);
      this.location =
        formatted || details.address1 || details.streetAddress || this.location;
      this.streetAddress = details.address1 || details.streetAddress || null;
      this.unit = details.address2;
      this.postalCode = details.postalCode;
      this.city = details.city;
      this.state =
        typeof details.state === "string"
          ? {
              id: details.state,
              label: details.state,
              code: details.state,
              stateprovcode: details.state,
            }
          : details.state;
      this.country = details.country;
    },
    close(): void {
      this.isOpen = false;
      this.suggestions = [];
      this.isOpenSuggestions = false;
    },
    async ResolveLocation(): Promise<void> {
      try {
        const resolvedLocation = await this.requestResolve({
          address: this.place?.address_components,
          originalAddress: this.place.formatted_address,
          company: this.place.name,
          type: 1,
        });

        if (resolvedLocation && !resolvedLocation.forLocation) {
          const result =
            this.place.reverseResolvedAddressComponents(resolvedLocation);
          if (result && result.changes && !this.isSavedAddress) {
            const newDetails = new Details();
            const allowedStates = mapState(this.states);
            newDetails.setStage(
              this.place,
              this.allowedCountries,
              allowedStates
            );
            newDetails.searchType = "full";
            newDetails.inputMethod = this.inputMethod; // BIK-2097: paste/type origin
            newDetails.rawInput = this.rawInput; // BIK-2097: exact entered text
            const countryCode =
              typeof newDetails.country === "string"
                ? newDetails.country
                : newDetails.country.code;
            for (let step = 0; step < this.allowedCountries.length; step++) {
              if (this.allowedCountries[step].code === countryCode) {
                newDetails.country = this.allowedCountries[step];
              }
            }
            const avsDetailsChecked = await avsCheck(newDetails);
            // BIK-1995: If avsCheck bailed out before reaching the backend
            // (missing required field like state), avsStatus.status stays
            // undefined. Mark the detail as failed so the inline guidance
            // shows errorAddress instead of silently accepting an
            // unverifiable address. Do NOT touch details where avsCheck
            // actually ran (avsStatus.status set to success or failed).
            // BIK-2127: migrated from the legacy AVS_SHIP1_REWORK flag to
            // avsRawPasteDirect — the inline guidance this feeds (avsUiState) is
            // driven by that flag, so without it an unverifiable address reads as
            // "idle". Flag OFF: emit as-is, matching pre-rework behavior.
            if ((this as any).avsRawPasteDirect) {
              const stage = avsDetailsChecked?.stageDetails;
              if (stage && stage.avsStatus?.status === undefined) {
                stage.avsChecked = true;
                stage.passedAvs = false;
                stage.avsProblem = true;
                if (!stage.avsStatus) {
                  stage.avsStatus = new AvsStatusEnum();
                }
                stage.avsStatus.status = AVSStatus.failed;
                stage.avsStatus.subStatus = AVSSubStatus.addressNotFound;
              } else if (stage && !stage.avsChecked) {
                // avsCheck ran successfully but didn't mark avsChecked (it only
                // sets status/subStatus). Flip the flag here so the StageForm
                // avsUiState computed reads the real result instead of "idle".
                stage.avsChecked = true;
              }
            }
            this.$emit("emitUserDetails", avsDetailsChecked);
            // this if should be check if necessary split the address or not
            if (
              this.place &&
              this.location !== this.place.formatted_address &&
              !this.hasAdvancedInStep
            ) {
              const formattedAddress = this.place.getFormattedAddress(avsDetailsChecked.stageDetails);
              if (formattedAddress) {
                // Create a new Place instance to avoid mutating store state
                // Use deep clone to break Vuex reactivity for nested objects
                const placeData = _.cloneDeep(this.place);
                placeData.formatted_address = formattedAddress;
                this.place = new Place(placeData);
              }

              // BIK-2125: when a Google suggestion is a business (Place sets `company` only
              // for store/establishment types, empty for residences — see Place ctor), show
              // the business name in the main input, consistent with how saved addresses show
              // their displayName ("Name: address"). Purely cosmetic on the Ship1 input; the
              // name already persists to details.company via setStage. The raw AVS value
              // (rawInput) is set separately in selectSuggestion, so this display prefix never
              // leaks into an AVS Region check. Flag-gated so legacy is unchanged.
              const businessName = (this as any).avsRawPasteDirect
                ? this.place?.company
                : "";
              this.location = businessName
                ? businessName + ": " + (formattedAddress || this.place?.formatted_address || "")
                : formattedAddress || this.place?.formatted_address || this.location;
            } else if (this.hasAdvancedInStep) {
              this.splitAddress();
            }
          }
        } else if (resolvedLocation && resolvedLocation.forLocation) {
          const result =
            this.place.reverseResolvedLocationComponents(resolvedLocation);
          const details: Details = new Details();
          const allowedStates = mapState(this.states);
          details.setStage(this.place, this.allowedCountries, allowedStates);
          details.postalCode = resolvedLocation.postal_code;
          details.searchType = "partial";
          details.inputMethod = this.inputMethod; // BIK-2097: paste/type origin
          details.rawInput = this.rawInput; // BIK-2097: exact entered text
          for (let step = 0; step < this.allowedCountries.length; step++) {
            if (this.allowedCountries[step].code === details.country.code) {
              details.country = this.allowedCountries[step];
            }
          }
          if (
            details.address1 &&
            details.postalCode &&
            details.state &&
            details.city &&
            details.country
          ) {
            details.searchType = "full";
            const avsDetailsChecked = await avsCheck(details);
            this.$emit("emitUserDetails", avsDetailsChecked);
          } else {
            // BIK-1995: Partial address (postal/city only, no street). Mark as
            // AVS-failed so the inline guidance shows the errorAddress message
            // ("Please enter a valid address!") instead of silently accepting
            // an unverifiable input.
            // BIK-2127: migrated from the legacy AVS_SHIP1_REWORK flag to
            // avsRawPasteDirect, which drives that inline guidance. Flag OFF:
            // leave unmarked, matching pre-rework behavior.
            //
            // BIK-2148: this is the GOOGLE path, and marking it failed was wrong here.
            // Partial addresses are allowed by design — the customer quotes on a city/ZIP
            // and gives the full street at step 3 — so a deliberately-picked Google
            // suggestion is not "an unverifiable input" the user must fix: its city/state/
            // postal come from address_components and are real. Master runs no AVS at all
            // for these (its completeness guard returns early), which is why production
            // shows no error; the synthetic failure here made Ship1 block a flow that is
            // supposed to continue, and opened the segmented fallback on top of it.
            //
            // Flag it as zone-only instead: avsChecked so the UI has something to say,
            // but NOT passedAvs (nothing was verified) and NOT avsProblem (nothing is
            // wrong). The raw-paste path is untouched — there the user's text is
            // uncontrolled, so BIK-2107's segmented fallback still applies.
            if ((this as any).avsRawPasteDirect) {
              details.avsChecked = true;
              details.passedAvs = false;
              details.avsProblem = false;
              if (!details.avsStatus) {
                details.avsStatus = new AvsStatusEnum();
              }
              details.avsStatus.status = AVSStatus.failed;
              details.avsStatus.subStatus = AVSSubStatus.zoneOnly;
            }
            this.$emit("emitUserDetails", {
              stageDetails: details,
              updatedDetails: details,
              needsAVSModal: false,
            });
          }
          // this if should be check if neccessary split the address or not

          if (
            this.place &&
            this.location !== this.place.formatted_address &&
            !this.hasAdvancedInStep
          ) {
            this.location = this.place
              ? (this.place.formatted_address as string)
              : this.location;
          } else if (this.hasAdvancedInStep) {
            this.splitAddress();
          }
        } else {
          const details: Details = new Details();
          const allowedStates = mapState(this.states);
          details.setStage(this.place, this.allowedCountries, allowedStates);
          details.inputMethod = this.inputMethod; // BIK-2097: paste/type origin
          details.rawInput = this.rawInput; // BIK-2097: exact entered text
          const postalCodeComponent = this.place.address_components
            ? this.place.address_components.find((component) =>
                component.types.includes("postal_code")
              )
            : false;
          details.postalCode = postalCodeComponent
            ? postalCodeComponent.long_name
            : "";
          details.searchType = "partial";
          this.$emit("emitUserDetails", {
            stageDetails: details,
            updatedDetails: details,
            needsAVSModal: false,
          });
          this.place.formatLabel(false, resolvedLocation);
          if (!this.hasAdvancedInStep) {
            this.location = this.place
              ? (this.place.formatted_address as string)
              : this.location;
          } else {
            this.splitAddress();
          }
        }
        this.$emit("emitLocation", this.place);
        this.suggestions = [];
        this.processing = false;
        this.isLoadingInput = false;
        this.skipEmitCheck = false;
        this.searchInputValue = "";
      } catch (e) {
        this.processing = true;
        this.skipEmitCheck = false;
        this.isLoadingInput = false;
      }
    },
    async resolveUserLocation(userAddress): Promise<void> {
      this.processing = true;
      let details = new Details();
      try {
        await this.reverseAddressComponents(userAddress, false);
        this.$emit("emitLocation", this.place);
        details = new Details(this.reverseDetailsComponents(userAddress));
        details.searchType = "full";
        details.isSavedAddress = true;
        details.inputMethod = this.inputMethod; // BIK-2097: "saved"
        details.rawInput = this.rawInput; // BIK-2097: saved address label
        details.avsStatus.source = DetailsSource.savedAddress;
        // BIK-2140: was two hand-rolled loops over allowedCountries/mapState(states) that
        // silently left the raw string behind when the lists were empty. Seed the fields from
        // the saved address first (it carries countryCode/country and state as plain strings),
        // then let the shared util match them against the lists.
        if (!details.country) {
          details.country = userAddress.countryCode || userAddress.country;
        }
        if (!details.state && userAddress.state) {
          details.state = userAddress.state;
        }
        resolveStateCountryObjects(details, this.allowedCountries, this.states);
        if (!details.passedAvs) {
          const avsDetailsChecked = await avsCheck(details);
          this.$emit("emitUserDetails", avsDetailsChecked);
        } else {
          this.$emit("emitUserDetails", {
            stageDetails: details,
            updatedDetails: details,
            changedFlags: false,
          });
        }
        const formattedAddress = this.place.getFormattedAddress(details);
        if (formattedAddress) {
          // Create a new Place instance to avoid mutating store state
          // Use deep clone to break Vuex reactivity for nested objects
          const placeData = _.cloneDeep(this.place);
          placeData.formatted_address = formattedAddress;
          this.place = new Place(placeData);
        }
        // TODO: improve this
        if (this.hasAdvancedInStep) {
          this.location = details.streetAddress ?? details.address1;
          this.city = details.city;
          this.country = details.country;
          this.state = details.state;
          this.postalCode = details.postalCode;
        } else {
          // BIK-2125: show the saved address's name in the main input (e.g. "George's
          // Cycles: address") so it matches what the saved-addresses dropdown shows for the
          // same entry. Use displayName ONLY — same condition as the dropdown label above
          // (`item.displayName ? item.displayName + ": " : ""`), no company fallback — so
          // the input and the list stay under one premise: a name shows only when a
          // displayName exists (Travis: showing it always is clutter — "only if we have
          // that info"). Flag-gated so legacy is unchanged.
          const savedName = (this as any).avsRawPasteDirect
            ? details.displayName
            : "";
          const savedAddress = this.place
            ? (this.place.formatted_address as string)
            : this.location;
          this.location = savedName
            ? savedName + ": " + savedAddress
            : savedAddress;
        }
        this.isLoadingInput = false;
        this.processing = false;
        this.suggestions = [];
        this.searchInputValue = "";
      } catch (error) {
        Sentry.captureException(error);
      }
    },
    async resolveEventLocation(userAddress): Promise<void> {
      this.processing = true;
      let details = new Details();
      try {
        await this.reverseAddressComponents(userAddress, false);
        this.$emit("emitLocation", this.place);
        details = new Details(this.reverseDetailsComponents(userAddress));
        details.searchType = "full";
        details.isEventAddress = true;
        details.inputMethod = this.inputMethod; // BIK-2097: "event"
        details.rawInput = this.rawInput; // BIK-2097: event address label
        details.passedAvs = true;
        details.avsStatus.source = DetailsSource.event;
        // BIK-2140: same degradation as the saved-address path above — seed from the event
        // address, then resolve against the lists through the shared util.
        if (!details.country) {
          details.country = userAddress.countryCode || userAddress.country;
        }
        if (!details.state && userAddress.state) {
          details.state = userAddress.state;
        }
        resolveStateCountryObjects(details, this.allowedCountries, this.states);
        this.$emit("emitUserDetails", {
          stageDetails: details,
          updatedDetails: details,
          needsAVSModal: false,
        });
        // TODO: improve this
        if (this.hasAdvancedInStep) {
          this.location = details.streetAddress ?? details.address1;
          this.city = details.city;
          this.country = details.country.name;
          this.state = details.state;
          this.postalCode = details.postalCode;
        } else {
          this.location = this.place
            ? (this.place.formatted_address as string)
            : this.location;
        }
        this.isLoadingInput = false;
        this.processing = false;
        this.suggestions = [];
      } catch (error) {
        Sentry.captureException(error);
      }
    },
    async reverseAddressComponents(
      userAddress,
      setAddress: boolean = true
    ): Promise<Place> {
      const _this = this;
      let address = "";
      if (userAddress) {
        if (userAddress.address1) {
          address += userAddress.address1;
        }
        if (userAddress.city) {
          address += (userAddress.address1 ? " " : "") + userAddress.city;
        }
        if (userAddress.state) {
          address += (userAddress.city ? ", " : "") + userAddress.state;
        }
        if (userAddress.postalCode) {
          address += (userAddress.state ? " " : "") + userAddress.postalCode;
        }
        if (userAddress.country) {
          address += (userAddress.postalCode ? ", " : "") + userAddress.country;
        }
      }
      if (setAddress) {
        this.location = address;
      }
      const autocompleteService = new google.maps.places.AutocompleteService();
      const predictions = await new Promise<any>((resolve, reject) => {
        autocompleteService.getPlacePredictions(
          { input: address },
          (predictions, status) => {
            if (
              status !== google.maps.places.PlacesServiceStatus.OK ||
              !predictions
            ) {
              reject(new Error("Failed to get predictions or status not OK."));
            }
            resolve(predictions);
          }
        );
      });
      const autocompleteOptions = {
        fields: [
          "formatted_address",
          "geometry",
          "name",
          "address_components",
          "vicinity",
          "type",
        ],
        strictBounds: false,
        placeId: predictions[0].place_id,
      };
      this.place = await this.getPlaceDetails(autocompleteOptions);

      _this.disabled = false;
      // this.place = this.autocomplete.getPlace() as Place;
      return this.place;
    },
    reverseDetailsComponents(userAddress): Details {
      const details = new Details();
      if (userAddress) {
        if (userAddress.companyName || userAddress.company)
          details.company = userAddress.companyName
            ? userAddress.companyName
            : userAddress.company;
        if (userAddress.firstName) details.firstName = userAddress.firstName;
        if (userAddress.lastName) details.lastName = userAddress.lastName;
        if (userAddress.streetAddress)
          details.streetAddress = userAddress.streetAddress;
        if (userAddress.address1) details.address1 = userAddress.address1;
        details.streetAddress = !details.streetAddress
          ? details.address1
          : details.streetAddress;

        if (userAddress.address2) details.address2 = userAddress.address2;
        if (userAddress.address3) details.address3 = userAddress.address3;
        if (userAddress.city) details.city = userAddress.city;
        if (userAddress.phone) details.phone = userAddress.phone;
        if (userAddress.country) {
          const countryName =
            typeof userAddress.country === "string"
              ? userAddress.country.trim().toLowerCase()
              : userAddress.country.name.trim().toLowerCase();
          const foundCountry = this.allowedCountries.find((country) => {
            const countryCode = String(country.code).trim().toLowerCase();
            const countryFullName = String(country.name).trim().toLowerCase();
            return (
              countryCode === countryName || countryFullName === countryName
            );
          });
          if (foundCountry) {
            details.country = foundCountry as Country;
          } else {
            details.country = userAddress.country;
          }
          this.filterStates = mapState(this.states);
        }
        if (userAddress.state) {
          if (typeof userAddress.state === "string") {
            if (this.filterStates[details.country.code]) {
              this.allowedState = this.filterStates[details.country.code];
              const foundAddress = this.allowedState.find((address) =>
                Object.values(address).includes(userAddress.state)
              );
              details.state = foundAddress;
            } else {
              details.state = userAddress.state;
            }
          } else {
            details.state = userAddress.state;
          }
        }
        details.passedAvs = userAddress.passedAvs;
        details.avsStatus.status = userAddress.passedAvs
          ? AVSStatus.success
          : AVSStatus.failed;
        if (userAddress.postalCode) details.postalCode = userAddress.postalCode;
        if (userAddress.addressId) details.addressId = userAddress.addressId;
        if (userAddress.displayName)
          details.displayName = userAddress.displayName;
      }
      return details;
    },
    splitAddress(streetOnly = false): void {
      // BIK-2107: streetOnly (used when returning to step 1 under avsRawPasteDirect):
      // the segmented grid is expanded, so putting the whole formatted address in the
      // main input duplicates the city/state/zip already shown in the fields below.
      // Show just the street line up top instead. Gated so legacy is unchanged.
      const useStreetOnly = streetOnly && (this as any).avsRawPasteDirect;
      // TODO: move to utils folder
      if (!this.$store.state.ship.stages.details) {
        this.place = new Place(
          this.$store.state.ship.stages.trips[this.id][this.direction]
        );
        this.location = this.place.formatted_address as string;
        // BIK-2107: mirror the street line into the dedicated Street Address field.
        this.streetAddress = this.place.getAddressProperty("route") || null;
        this.unit = this.place.getAddressProperty("unit");
        this.postalCode = this.place.getAddressProperty("postal_code");
        this.city = this.place.getAddressProperty("locality");
        this.state = this.place.getAddressProperty(
          "administrative_area_level_1"
        );
        this.country = this.place.getAddressProperty("country");
        this.isSingle = false;
        // BIK-2127: see the hasAdvancedInStep watcher — the legacy !!unit branch was dead.
        this.isAddressExpanded = true;
        this.showArrowBtn = true;
      } else if (this.$store.state.ship.stages.details) {
        const details = new Details(
          this.$store.state.ship.stages.details?.[this.id]?.[this.direction]
        );
        // BIK-2107: incomplete-paste fallback. When a one-line paste couldn't be
        // resolved (addressIncomplete), AVS returns empty city/state, so the
        // formatted string collapses to "123 alamo plaza, , US". Keep the user's
        // raw text in the main input and leave the segmented fields empty for them
        // to fill. Only under the new flag + this substatus — every other path keeps
        // the pre-existing formatted_address behavior (backward compatible).
        const isIncompletePaste =
          (this as any).avsRawPasteDirect &&
          details.avsStatus?.subStatus === AVSSubStatus.addressIncomplete;
        if (isIncompletePaste) {
          this.location =
            details.rawInput || details.address1 || details.streetAddress;
        } else if (useStreetOnly && !details.passedAvs) {
          // BIK-2107: expanded grid already shows city/state/zip — keep just the
          // street line in the main input instead of the full formatted address.
          // BIK-2106: only while still unverified — once AVS has passed, returning to
          // step 1 should show the full formatted address like any other verified
          // address, not a lone street line next to a green "Verified" badge.
          this.location =
            details.address1 ||
            details.streetAddress ||
            Place.getFormattedAddress(details);
        } else {
          const formatted = Place.getFormattedAddress(details);
          this.location = formatted
            || (details.address1 ? details.address1 : details.streetAddress);
        }
        // BIK-2107: mirror the street line into the dedicated Street Address field.
        // On an incomplete paste, leave it EMPTY (yellow, like city/state) so the user
        // fills it in — the raw one-liner stays in the main input above, not here.
        this.streetAddress = isIncompletePaste
          ? null
          : details.address1 || details.streetAddress || null;
        this.unit = details.address2;
        this.postalCode = details.postalCode;
        this.city = details.city;
        this.state =
          typeof details.state === "string"
            ? {
                id: details.state,
                label: details.state,
                code: details.state,
                stateprovcode: details.state,
              }
            : details.state;

        this.country = details.country;
        this.isSingle = false;
        // BIK-2127: see the hasAdvancedInStep watcher — the legacy !!unit branch was dead.
        this.isAddressExpanded = true;
        this.showArrowBtn = true;
        this.filterStates = mapState(this.states);
        this.allowedState = this.filterStates?.[this.country?.code];
      }
    },
    checkTrip() {
      // BIK-2125: a password-manager/autofill (1Password) fires a transient blur DURING
      // the autofill and then RETURNS focus to the input; a real user click-outside leaves
      // focus gone. Wait past that transient, then act on the settled focus state:
      //   - focus back in the main input  → autofill blur-and-return, user is still in the
      //     field (Google suggestions showing) → do NOT run the deferred AVS.
      //   - focus stayed out              → real click-outside → honor the deferred AVS
      //     Region check (skipped earlier by the focus-still-inside gate in editAddress),
      //     unless a Google suggestion was picked (skipEmitCheck). 250ms clears the
      //     observed autofill blur/return and lets the gated change set pendingMainInputAvs.
      setTimeout(() => {
        const focusBackInInput =
          document.activeElement === (this.$refs.input as HTMLElement);
        if (
          this.pendingMainInputAvs &&
          !this.skipEmitCheck &&
          !focusBackInInput &&
          (this as any).avsRawPasteDirect &&
          this.isSingle &&
          this.location
        ) {
          this.pendingMainInputAvs = false;
          this.emitMainInputAddress(this.location, "address1");
        }
        // BIK-2125: if focus returned to the input (autofill), leave pendingMainInputAvs
        // set — a later real click-outside blur re-runs this and honors it then.
        if (!this.skipEmitCheck) {
          this.$emit("emitCheckTrip", {
            id: this.id,
            direction: this.direction,
          });
        }
      }, 250);
    },
    async editAddress($event, element): Promise<void> {
      // BIK-2125: capture the focus state at the moment of the native `change` event
      // (synchronously, before the await below — the focus can move during the 200ms).
      // A native `change` on a text input fires when the value changed AND the input
      // loses focus (or on Enter): a real user blur arrives with focus already gone
      // (activeElement !== this input). Password managers / autofill (1Password, etc.)
      // set the value and fire `change` programmatically WITHOUT moving focus — so the
      // input is still focused. See the gate below.
      const focusStillInMainInput =
        (this as any).avsRawPasteDirect &&
        this.isSingle &&
        element === "address1" &&
        document.activeElement === (this.$refs.input as HTMLElement);
      await new Promise((resolve) => setTimeout(resolve, 200));
      // BIK-2125: Google-lookup-has-priority gate. When a `change` on the main single
      // input fires while focus is still inside it, the user did NOT deliberately leave
      // the field — this is a password-manager/autofill programmatic change (it drops
      // just the street, e.g. "123 Alamo Plz", into the field). Running AVS Region on it
      // now fails as incomplete, which closes the Google suggestions and shows the
      // "please complete the address" fallback — exactly the flicker we want to avoid.
      // Skip the AVS emit here: the user is still in the field (may pick a Google
      // suggestion or keep typing). But mark it pending so a later real blur WITHOUT
      // picking a suggestion still verifies — the browser won't re-fire `change` on that
      // blur (the value didn't change since this programmatic change), so checkTrip()
      // must trigger it. Paste has its own explicit path (runPasteToAvs) and is not
      // affected. Guarded to the flag + single main input so legacy/segmented are unchanged.
      if (focusStillInMainInput) {
        this.pendingMainInputAvs = true;
        return;
      }
      // Note: the promise is used to allow time for the @click functions to execute first
      // so by editing the flag skipEmitCheck and can check if necessary skip the emit
      if (!this.skipEmitCheck) {
        // BIK-2107: a paste already emitted address1 + ran AVS Region. The browser then
        // fires a native `change` on the same input with the identical value, which would
        // re-emit and run AVS a second time. Skip that duplicate by value (no fragile
        // timing): if address1 is unchanged from what the paste captured, do nothing.
        if (
          (this as any).avsRawPasteDirect &&
          element === "address1" &&
          this.inputMethod === "paste" &&
          $event.target.value === this.rawInput
        ) {
          return;
        }
        if (element === "address1") {
          // BIK-2125: this change emitted AVS directly (real blur / focus already gone),
          // so there's nothing pending for checkTrip() to re-fire.
          this.pendingMainInputAvs = false;
        }
        this.emitMainInputAddress($event.target.value, element);
      }
    },
    // BIK-2125: shared emit for a one-line address entered in the main single input,
    // used both by the `change` handler (real blur) and by checkTrip() (blur after a
    // gated autofill change). Marks it as a manual single-line entry so getAvsParams
    // verifies it via AVS Region, mirroring the paste path.
    emitMainInputAddress(value: string, element: string): void {
      // BIK-2097: a hand-edit of the street field counts as manual entry.
      // Editing secondary fields (address2/city/postal) after a paste/type
      // must not overwrite how address1 was originally entered.
      if (element === "address1") {
        // BIK-2095: a one-line entry typed into the single main input (flag on, not the
        // expanded segmented form) is verified via AVS Region, same as a paste. Mark it
        // distinctly so getAvsParams can tell it apart from editing a segmented field.
        this.inputMethod =
          (this as any).avsRawPasteDirect && this.isSingle
            ? "manual-single"
            : "manual";
        this.rawInput = value; // BIK-2097: exact typed text
        // BIK-2106: a hand-edit that reaches here is a real manual change (not the
        // programmatic v-model update ResolveLocation makes on a Google pick, which
        // never fires a native `change`) — back to "Type or paste".
        this.googleModeActive = false;
      }
      this.$emit("emitChangeAddress", {
        id: this.id,
        direction: this.direction,
        element,
        value,
        inputMethod: this.inputMethod,
        rawInput: this.rawInput,
      });
    },
    async getPlaceDetails(autocompleteOptions): Promise<Place> {
      const service = new this.googleObj.maps.places.PlacesService(
        document.createElement("div")
      );
      return await new Promise((resolve, reject) => {
        service.getDetails(autocompleteOptions, (place, status) => {
          if (status === this.googleObj.maps.places.PlacesServiceStatus.OK) {
            resolve(new Place(place));
          } else {
            reject(
              new Error(`Failed to get place details with status: ${status}`)
            );
          }
        });
      });
    },
    fetchSuggestions() {
      if (!this.$refs?.input) {
        return;
      }
      const input = (this.$refs.input as HTMLInputElement)?.value
        ? (this.$refs.input as HTMLInputElement).value
            .toLowerCase()
            .replace(/[,.]/g, "")
        : "";

      if (input.length < 3) {
        // BIK-2108: seed from the preferred-first ordered list so the preferred sits on top.
        this.filteredUserAddress = (this as any).orderedUserAddresses;
        this.suggestions = [];
        this.isLimit = false;
        return;
      }
      this.isLimit = true;
      // BIK-2108: filter over the ordered list so a matching preferred still ranks first.
      this.filteredUserAddress = (this as any).orderedUserAddresses.filter((address) => {
        const addressString = `${address.address1} ${address.city} ${
          address.state
        } ${address.postalCode} ${address.companyName || ""}`.toLowerCase();
        return (
          input.split(" ").every((term) => addressString.includes(term)) ||
          address?.displayName?.includes(input)
        );
      });
      const autocompleteService = new google.maps.places.AutocompleteService();
      autocompleteService.getPlacePredictions(
        { input },
        (predictions, status) => {
          if (
            status !== google.maps.places.PlacesServiceStatus.OK ||
            !predictions
          ) {
            this.suggestions = [];
            return;
          }
          this.suggestions = predictions;
        }
      );
    },
    selectSuggestion(suggestion) {
      this.pendingMainInputAvs = false; // BIK-2125: picked Google → drop any deferred AVS
      this.inputMethod = "google"; // BIK-2097: typed, then picked a Google Places suggestion
      // BIK-2097: capture what the user typed (and the suggestion they picked)
      // before Google Places resolves it.
      this.rawInput =
        (this.$refs.input as HTMLInputElement)?.value ||
        suggestion?.description ||
        null;
      this.skipEmitCheck = true;
      this.isLoadingInput = true;
      this.needSetAddress = false;
      this.processing = true;
      const autocompleteOptions = {
        fields: [
          "formatted_address",
          "geometry",
          "name",
          "address_components",
          "vicinity",
          "type",
        ],
        strictBounds: false,
        placeId: suggestion.place_id,
      };
      const service = new this.googleObj.maps.places.PlacesService(
        document.createElement("div")
      );
      service.getDetails(autocompleteOptions, (place, status) => {
        if (status === this.googleObj.maps.places.PlacesServiceStatus.OK) {
          setTimeout(() => {
            this.place = new Place(place);
            this.ResolveLocation();
          }, 200);
        }
      });
      this.suggestions = [];
      this.isOpenSuggestions = false;
    },
    navigateSuggestions(direction) {
      // BIK-2106: saved addresses may be hidden (typing a Google lookup), so keyboard
      // navigation must only span what's actually rendered — else arrow-down lands on
      // invisible saved rows.
      const savedCount = (this as any).showSavedAddresses
        ? this.filteredUserAddress.length
        : 0;
      const itemListLength = this.suggestions.length + savedCount;
      if (direction === "down") {
        if (this.highlightedIndex < itemListLength - 1) {
          this.highlightedIndex++;
        } else {
          this.highlightedIndex = 0;
        }
      } else if (direction === "up") {
        if (this.highlightedIndex > 0) {
          this.highlightedIndex--;
        } else {
          this.highlightedIndex = itemListLength - 1;
        }
      }
    },
    selectHighlightedSuggestion() {
      if (
        this.highlightedIndex >= 0 &&
        this.suggestions[this.highlightedIndex]
      ) {
        this.selectSuggestion(this.suggestions[this.highlightedIndex]);
        this.highlightedIndex = -1;
      }
    },
    // BIK-2095/BIK-2106: shared direct-paste-to-AVS body. Takes the pasted/clipboard text,
    // collapses it to a single line (AVS Region needs one line), puts it in the field, and
    // emits it so the store runs avsCheck in Region mode with inputMethod="paste". Called by
    // the native @paste handler and by the Paste icon (navigator.clipboard.readText). Also
    // lights the Paste icon as the detected method.
    runPasteToAvs(text: string): void {
      this.pendingMainInputAvs = false; // BIK-2125: paste verifies now, nothing deferred
      this.inputMethod = "paste"; // BIK-2097: address pasted into the field
      this.closeSuggestions();
      this.googleModeActive = false; // BIK-2106: a paste is not Google mode
      this.pasteModeActive = true; // BIK-2106: highlight the Paste icon
      // BIK-2095: a multi-line pasted address (company / street / city-state-zip on separate
      // lines) must collapse to one line with spaces — AVS Region needs a single-line string,
      // and run-together lines ("COMPOSITES59") fail to parse.
      const singleLine = toSingleLine(text);
      this.rawInput = singleLine; // BIK-2097: exact pasted text (normalized)
      this.location = singleLine;
      // Emit the paste directly so the store runs avsCheck (region mode) keeping
      // inputMethod="paste". A follow-up blur may re-fire @change → a second region check
      // with the same string; it's idempotent (same result), so no fragile suppression timing.
      this.$emit("emitChangeAddress", {
        id: this.id,
        direction: this.direction,
        element: "address1",
        value: singleLine,
        inputMethod: "paste",
        rawInput: singleLine,
      });
    },
    // BIK-2106: explicit Paste icon click — read the clipboard and run the same direct-AVS
    // flow as a native Ctrl+V. Requires the async Clipboard API (user gesture + permission);
    // if it's unavailable or denied, fall back to opening the field for a manual Ctrl+V.
    async onPasteIconClick(): Promise<void> {
      try {
        const text = (await navigator.clipboard.readText())?.trim();
        if (text) {
          // BIK-2106: pasting from the expanded (segmented) view starts fresh — collapse
          // back to the single input and clear the old segmented fields (same as the Google
          // icon), so the pasted address isn't reconciled against stale fields. clearInput()
          // also resets showArrowBtn → the base view (Saved ▼, no −/+). Then paste.
          if (!this.isSingle) {
            this.clearInput();
          }
          this.runPasteToAvs(text);
          return;
        }
      } catch (e) {
        // Clipboard read blocked (permission/unsupported) — just focus the field so the
        // user can Ctrl+V manually, which hits the native @paste handler.
      }
      this.pasteModeActive = true;
      this.googleModeActive = false;
      if (this.$refs?.input) {
        (this.$refs.input as HTMLInputElement).focus();
      }
    },
    async pasteAddress(event) {
      // BIK-2106: in "Google" mode a paste is search text, not an address to verify
      // directly — let the browser paste natively (no preventDefault) so it lands in
      // the field like any typed character; the main input's @input handler already
      // calls fetchSuggestions() while googleModeActive, same as if the user had typed
      // it. Skip the BIK-2095 paste-to-AVS-Region path entirely for this case.
      if ((this as any).avsRawPasteDirect && this.googleModeActive) {
        return;
      }
      try {
        const pastedText = event.clipboardData.getData("text").trim();
        // BIK-2095: keep exactly what the user pasted and verify it directly via AVS
        // (UPS XAV Region mode), instead of turning it into a Google Places long-form
        // address and running the replace/cleanup. The pasted value may already be AVSed.
        if ((this as any).avsRawPasteDirect) {
          event.preventDefault();
          this.runPasteToAvs(pastedText);
          return;
        }
        this.inputMethod = "paste"; // BIK-2097: address pasted into the field
        this.rawInput = pastedText; // BIK-2097: exact pasted text
        this.skipEmitCheck = true;
        this.isLoadingInput = true;
        const autocompleteService =
          new google.maps.places.AutocompleteService();
        const predictions = await new Promise<any>((resolve, reject) => {
          autocompleteService.getPlacePredictions(
            { input: pastedText },
            (predictions, status) => {
              if (
                status !== google.maps.places.PlacesServiceStatus.OK ||
                !predictions
              ) {
                reject(
                  new Error("Failed to get predictions or status not OK.")
                );
              }
              resolve(predictions);
            }
          );
        });
        const autocompleteOptions = {
          fields: [
            "formatted_address",
            "geometry",
            "name",
            "address_components",
            "vicinity",
            "type",
          ],
          strictBounds: false,
          placeId: predictions[0].place_id,
        };
        this.closeSuggestions();
        this.place = await this.getPlaceDetails(autocompleteOptions);
        await this.ResolveLocation();
      } catch (error) {
        this.skipEmitCheck = false;
        this.isLoadingInput = false;
        this.checkTrip();
      }
    },
    openSuggestionBox() {
      // BIK-2108: preferred-first order so the Saved ▼ list shows the preferred address on top.
      this.filteredUserAddress = (this as any).orderedUserAddresses;
      this.isOpenSuggestions = true;
      // BIK-2106: opened via an explicit action (Saved ▼ / focus), so the panel may show
      // even if empty. Typing (onMainInput) resets this so it won't pre-show an empty tray.
      this.suggestionsOpenedExplicitly = true;
      // BIK-2106: opening the Saved ▼ list is "show me my saved addresses to pick one",
      // NOT a search. Under the flag, do NOT seed the search buffer with the current
      // (possibly already-verified) address and do NOT run fetchSuggestions() against it —
      // that filtered the saved list down to ~0 matches ~500ms after opening, so the list
      // the user opened to use vanished on its own. Keep the full saved list intact; the
      // user narrows it by typing in the main input (onMainInput → Google mode). We also
      // don't steal focus into the input here — it still holds the verified address, and
      // focusing it adds nothing for a pick-from-list action.
      if ((this as any).avsRawPasteDirect) {
        return;
      }
      if (this.location) {
        this.searchInputValue = this.location;
      }
      setTimeout(() => {
        if (this.location) {
          this.fetchSuggestions();
        }
        if (this.$refs?.input) {
          (this.$refs.input as HTMLInputElement).focus();
        }
      }, 500);
    },
    closeSuggestions() {
      this.isOpenSuggestions = false;
      this.suggestions = [];
      this.highlightedIndex = -1;
      this.searchInputValue = "";
      this.skipEmitCheck = false;
      this.suggestionsOpenedExplicitly = false; // BIK-2106
    },
    checkAddress() {
      setTimeout(() => {
        // BIK-2095: with the editable main input, the user types/pastes straight into
        // `location`, so don't clobber it with an empty dropdown-search value on blur.
        // Only copy searchInputValue → location when the dropdown search was actually used.
        if ((this as any).avsRawPasteDirect && !this.searchInputValue) {
          return;
        }
        if (this.needSetAddress) {
          this.location = this.searchInputValue;
        }
      }, 200);
    },
    clearInput() {
      this.skipEmitCheck = true;
      this.pendingMainInputAvs = false; // BIK-2125: no deferred AVS after a clear
      this.closeSuggestions();
      this.googleModeActive = false; // BIK-2106: reset input-method highlight
      this.pasteModeActive = false; // BIK-2106: reset input-method highlight
      this.location = "";
      this.streetAddress = null; // BIK-2107: reset dedicated street field
      this.state = { id: "", label: "-" };
      this.city = "";
      this.country = { id: "", label: "-" };
      this.postalCode = "";
      this.unit = "";
      this.searchInputValue = "";
      this.isSingle = true;
      this.isAddressExpanded = false;
      this.showArrowBtn = false;
      this.inSegmentedFallback = false; // BIK-2107: reset fallback UI state
      // BIK-2108: an X on the Ship From kills auto-prefill for the rest of the session, so the
      // preferred address isn't re-filled after the user clears it to enter a different one.
      if ((this as any).avsRawPasteDirect && this.direction === "from") {
        this.preferredCleared = true;
      }
      this.$emit("emitClearInput");
    },
    // BIK-2106: typing in the main input is Google mode — light the Google icon (drop the
    // Paste highlight) and run live suggestions. BIK-2095 kept typing from auto-searching by
    // default, so this only fetches once googleModeActive is on (set here on the first
    // keystroke). Must open the suggestions dropdown (isOpenSuggestions) — fetchSuggestions()
    // only fills the list; the <ul> that renders it is gated on isOpenSuggestions, so without
    // this the predictions load but never show. No-op under legacy (input only mounts on flag).
    onMainInput(): void {
      this.googleModeActive = true;
      this.pasteModeActive = false;
      this.isOpenSuggestions = true;
      // BIK-2106 (Bill): typing must not pre-show an empty tray — the panel stays hidden
      // until fetchSuggestions produces results (≥3 chars). Not an explicit open.
      this.suggestionsOpenedExplicitly = false;
      // BIK-2108: preferred-first order (fetchSuggestions below re-derives from the ordered list too).
      this.filteredUserAddress = (this as any).orderedUserAddresses;
      this.fetchSuggestions();
    },
    // BIK-2106: "Google" icon click. If the segmented fallback form is showing
    // (BIK-2107, e.g. incomplete-paste), a Google search starts over rather than
    // trying to reconcile Google's result with whatever partial fields are there —
    // clearInput() collapses back to the single input and drops the fallback message,
    // then Google mode opens on a clean slate.
    onGoogleIconClick(): void {
      if (this.googleModeActive) {
        this.closeSuggestions();
        return;
      }
      this.pasteModeActive = false;
      if (!this.isSingle) {
        this.clearInput();
      }
      this.googleModeActive = true;
      this.openSuggestionBox();
      // BIK-2106 (Bill): the Google icon puts the field in search mode to TYPE — it must
      // not pre-show an empty tray. Unlike the Saved ▼ icon, this isn't an explicit
      // "show me the list" open, so keep the panel hidden until suggestions arrive.
      this.suggestionsOpenedExplicitly = false;
    },
    // BIK-2106: "Saved Addresses" (▼) icon click. Preserves the pre-icons chevron behavior:
    // once the segmented form is expanded (showArrowBtn), it toggles that grid (onShow);
    // otherwise it toggles the saved-addresses/suggestions dropdown, same as the old arrow.
    onSavedIconClick(): void {
      if (this.showArrowBtn) {
        this.onShow();
        return;
      }
      if (this.isOpenSuggestions) {
        this.closeSuggestions();
      } else {
        this.openSuggestionBox();
      }
    },
    countrySelectionMade(selection: any) {
      const elements = selection.name.split("-");
      if (elements.length > 1) {
        // if difference is found
        if (
          selection.option &&
          this.country &&
          selection.option.code !== this.country.code
        ) {
          this.state = { id: "", label: "" };
          this.$emit("emitChangeAddress", {
            id: this.id,
            direction: this.direction,
            element: "state",
            value: { id: "", label: "" },
          });
          this.country = selection.option;
          this.$emit("emitChangeAddress", {
            id: this.id,
            direction: this.direction,
            element: "country",
            value: selection.option,
          });
        }
      }
    },
    stateSelectionMade(selection: any) {
      const elements = selection.name.split("-");
      if (elements.length > 1) {
        // if difference is found
        if (
          selection.option &&
          this.state &&
          selection.option.stateprovcode !== this.state?.stateprovcode
        ) {
          this.state = selection.option;
          this.$emit("emitChangeAddress", {
            id: this.id,
            direction: this.direction,
            element: "state",
            value: selection.option,
          });
        }
        this.state = selection.option;
      }
    },
    parseStateLabel,
  },
});
